import { useMemo } from 'react';

import useAuth from '@/api/auth/useAuth';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { useIsModuleEnabled } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { useRoutePermissions } from '@/hooks/authz/useRoutePermissions';
import { routeTree } from 'src/routes';

import type { AccessContext } from '@/routing/access-meta';

export function useAccessContext(): AccessContext {
  const { isModuleEnabled } = useIsModuleEnabled();
  const { currentUser } = useAuth();
  const { viewMode } = useViewMode();

  const { permissions, isLoading: permissionsLoading } =
    useRoutePermissions(routeTree);

  // Feature flags used to hide routes today
  const { enabled: showEmailPoMetricsEnabled } = useFeatureFlag(
    'show-email-po-metrics-enabled',
    false,
  );
  const { enabled: agentMonitorEnabled } = useFeatureFlag(
    'agent-monitor-enabled',
    false,
  );
  const { enabled: itemMasterUiEnabled } = useFeatureFlag(
    'item-master-ui-enabled',
    false,
  );
  const { enabled: inboxEnabled } = useFeatureFlag('inbox-enabled', false);
  const { enabled: chatAgentEnabled } = useFeatureFlag(
    'chat-agent-enabled',
    false,
  );

  // Every flag a route gates on must be registered here — an unregistered flag
  // resolves to false, which silently hides the route from the sidebar, the
  // quick-search palette, and navigation settings.
  const featureFlags = useMemo<Record<string, boolean>>(
    () => ({
      'show-email-po-metrics-enabled': showEmailPoMetricsEnabled,
      'agent-monitor-enabled': agentMonitorEnabled,
      'item-master-ui-enabled': itemMasterUiEnabled,
      'inbox-enabled': inboxEnabled,
      'chat-agent-enabled': chatAgentEnabled,
    }),
    [
      showEmailPoMetricsEnabled,
      agentMonitorEnabled,
      itemMasterUiEnabled,
      inboxEnabled,
      chatAgentEnabled,
    ],
  );

  const userIsAdmin = useMemo(
    () => isAdmin(currentUser, viewMode),
    [currentUser, viewMode],
  );

  return {
    isAdmin: userIsAdmin,
    isFeatureEnabled: (flag: string) => featureFlags[flag] ?? false,
    isModuleEnabled,
    permissions,
    permissionsLoading,
  };
}
